import React, { useState, useCallback } from 'react';
import { PlayCircle, X, Loader2, AlertTriangle } from 'lucide-react';

// =====================================================================
// O vídeo do procedimento, DENTRO da tela — o "como fazer".
//
// Componente COMPARTILHADO: mora em `src/components/` porque serve qualquer
// módulo (é a regra que o `ImagemSharePoint` já enuncia — nasceu na Manutenção e
// o comentário lá diz que, ao ser usado por um terceiro módulo, o lugar dele
// passa a ser esta pasta). Hoje o usam o Lançamento de Petty Cash, o
// Registro de Nulos, o Controle do Troco e a Solicitação de Estorno (os quatro
// no Financeiro) e a Videoteca da Biblioteca.
//
// Fica RECOLHIDO por padrão e abre no lugar — não em pop-up nem em aba nova:
// quem está preenchendo assiste com o formulário à vista e continua.
//
// ⚠️ POR QUE NÃO É UM `ImagemSharePoint` DA VIDA: aquele busca os bytes com
//    `fetch` autenticado, monta um blob e exibe. Para foto de 300 KB é perfeito;
//    para vídeo obrigaria a baixar 6–8 MB INTEIROS antes da primeira imagem e a
//    guardar tudo na memória do aparelho — que na loja é compartilhado.
//    Aqui pedimos ao backend um LINK ASSINADO (rota autenticada) e entregamos
//    esse link ao `<video src>`, que passa a tocar progressivamente e a permitir
//    arrastar a barra, porque o proxy responde 206/`Range`.
//    Ver server/videos-procedimento.js.
//
// ⚠️ O link vale 10 MINUTOS. Se a tela ficar aberta e o play vier depois disso,
//    o player falha — e o `onError` oferece "tentar de novo", que só pede outro
//    link. Sem isso o sintoma seria um quadrado preto sem explicação.
//
// ⚠️ `preload="metadata"`: baixa só o cabeçalho (duração/dimensões) e espera o
//    play. Com `auto`, abrir a tela puxaria o vídeo todo de graça — em 13 lojas,
//    todo dia, para um vídeo que talvez ninguém abra.
//
// ⚠️ VÍDEO AUSENTE NÃO É ERRO: se o `GET /link` devolver 404 (chave sem arquivo
//    ainda, ou vídeo tirado do ar), o componente **não renderiza nada**. É o que
//    permite deixar o `<VideoProcedimento>` na tela antes de o arquivo existir.
//
// Props:
//   apiBase — prefixo do módulo DONO do vídeo (ex.: '/api/financeiro/videos').
//             É obrigatório e é por módulo: cada um tem o seu proxy, com o seu
//             confinamento de pasta no SharePoint e a sua regra de acesso.
//   chave   — a chave do vídeo na dtbVideoProcedimento (ex.: 'nulos-registro').
//   titulo  — rótulo do botão. Se omitido, usa o que o backend devolver.
//   cor     — a cor do MÓDULO (docs/ARQUITETURA.md › Paleta dos módulos).
//   rotuloBotao — texto do botão recolhido. O padrão ("Como fazer: <título>") é o
//             da TELA DE TRABALHO, onde o vídeo é uma oferta no meio do
//             formulário. Na VIDEOTECA (Biblioteca) o card já mostra o título, e
//             repeti-lo no botão só faria ruído — lá vem "Assistir".
//             ⚠️ É prop, e não um segundo componente: o que interessa aqui (link
//             de 10 min, retry, "vídeo ausente some da tela") é justamente o que
//             não se pode ter em duas cópias.
// =====================================================================

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const VideoProcedimento = ({ apiBase, chave, titulo = '', cor = '#475569', rotuloBotao = '' }) => {
  const [aberto, setAberto] = useState(false);
  const [url, setUrl] = useState('');
  const [rotulo, setRotulo] = useState(titulo);
  // fechado | carregando | pronto | erro | ausente
  const [estado, setEstado] = useState('fechado');

  const pedirLink = useCallback(async () => {
    setEstado('carregando');
    try {
      const r = await fetch(`${apiBase}/link?chave=${encodeURIComponent(chave)}`, { headers: auth() });
      // 404 = não há vídeo para esta chave. Não é falha: o componente se apaga.
      if (r.status === 404) { setEstado('ausente'); return; }
      if (!r.ok) throw new Error();
      const d = await r.json();
      setUrl(d.url);
      if (d.titulo) setRotulo(d.titulo);
      setEstado('pronto');
    } catch {
      setEstado('erro');
    }
  }, [apiBase, chave]);

  const abrir = () => { setAberto(true); pedirLink(); };

  const fechar = () => {
    setAberto(false);
    setEstado('fechado');
    setUrl(''); // solta o player: o vídeo para de baixar ao recolher
  };

  // Vídeo não disponível: some da tela sem deixar botão morto.
  if (estado === 'ausente') return null;

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={abrir}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
          border: `1px solid ${cor}`, background: '#fff', color: cor,
          fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
        }}
        title="Assistir ao procedimento sem sair da tela"
      >
        <PlayCircle size={15} /> {rotuloBotao || `Como fazer${rotulo ? `: ${rotulo}` : ''}`}
      </button>
    );
  }

  return (
    <div style={{
      marginTop: 10, background: '#0f172a', borderRadius: 10,
      overflow: 'hidden', border: `1px solid ${cor}`,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 8, padding: '8px 12px', background: cor, color: '#fff',
      }}>
        <span style={{ fontSize: 12, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <PlayCircle size={15} /> {rotulo || 'Procedimento'}
        </span>
        <button
          type="button" onClick={fechar} title="Fechar o vídeo"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            border: 'none', background: 'rgba(255,255,255,.15)', color: '#fff',
            borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
            fontWeight: 700, fontSize: 11, fontFamily: 'inherit',
          }}
        >
          <X size={13} /> Fechar
        </button>
      </div>

      {/* ⚠️ A animação vem COM o componente: o `.spin` do projeto é declarado tela
          por tela (não existe em CSS global), e um componente compartilhado não
          pode depender de a hospedeira ter definido a dele. Classe própria para
          não colidir com o `.spin` de quem já tem. */}
      <style>{'.vp-spin{animation:vp-spin 1s linear infinite}@keyframes vp-spin{to{transform:rotate(360deg)}}'}</style>

      {estado === 'carregando' && (
        <div style={{ padding: 28, textAlign: 'center', color: '#cbd5e1', fontSize: 13 }}>
          <Loader2 size={20} className="vp-spin" /> <span style={{ marginLeft: 8 }}>Preparando o vídeo…</span>
        </div>
      )}

      {estado === 'erro' && (
        <div style={{ padding: 20, textAlign: 'center', color: '#fecaca', fontSize: 13 }}>
          <AlertTriangle size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
          Não foi possível carregar o vídeo.
          <button
            type="button" onClick={pedirLink}
            style={{
              marginLeft: 10, border: '1px solid #fecaca', background: 'transparent',
              color: '#fecaca', borderRadius: 6, padding: '4px 10px',
              cursor: 'pointer', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
            }}
          >
            Tentar de novo
          </button>
        </div>
      )}

      {estado === 'pronto' && url && (
        <video
          src={url}
          controls
          preload="metadata"
          // `playsInline` impede o iPhone de jogar o vídeo em tela cheia ao dar
          // play — no meio de um formulário isso tira a pessoa do lugar.
          playsInline
          controlsList="nodownload"
          onError={() => setEstado('erro')}
          style={{ display: 'block', width: '100%', maxHeight: 420, background: '#000' }}
        />
      )}
    </div>
  );
};

export default VideoProcedimento;
